import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import ProductPriceControls from './ProductPriceControls';
import InvestmentRateSelect from './InvestmentRateSelect';
import { createInvestmentProduct, type InvestmentProduct } from '@/editor/investmentProduct';
import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BookmarkPlus,
  ChevronDown,
  Copy,
  ExternalLink,
  Trash2,
  X,
} from 'lucide-react';
import { confirmAction } from '@/lib/confirm';
import { toast } from '@/lib/toast';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { pricingTypes, type PricingType } from '@/theme/uboTheme';
import {
  useInvestmentInspectorStore,
  type InvestmentInspectorGroup,
  type InvestmentInspectorPhase,
  type InvestmentInspectorRow,
  type InvestmentRowVersionChange,
} from '@/store/investmentInspectorStore';
import {
  outdatedRowVersion,
  useProductVersionStore,
  type OutdatedRowVersion,
} from '@/store/productVersionStore';
import { formatCurrency, isCurrencyAmount, parseCurrencyAmount } from '@/lib/format';
import productApi from '@/lib/productApi';
import {
  changedFields,
  productLatestForRow,
  productOriginalForRow,
  type ProductOriginalValues,
} from '@/lib/productOriginalValues';
import type { ProductSnapshot } from '@/types/product';
import type { InvestmentCurrency, InvestmentLabelLanguage } from '@/types';

const CURRENCY_OPTIONS: { value: InvestmentCurrency; label: string }[] = [
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'GBP', label: 'Pond (£)' },
  { value: 'USD', label: 'Dollar ($)' },
];

const LABEL_LANG_OPTIONS: { value: InvestmentLabelLanguage; label: string }[] = [
  { value: 'nl', label: 'NL' },
  { value: 'en', label: 'EN' },
];

function Section({
  title,
  children,
  horizontal,
}: {
  title?: string;
  children: ReactNode;
  horizontal?: boolean;
}) {
  return (
    <div
      className={cn(
        horizontal
          ? 'min-w-[240px] max-w-[360px] flex-1 border-l border-gray-200 pl-5 first:border-l-0 first:pl-0'
          : 'border-t border-gray-200 px-4 py-4 first:border-t-0'
      )}
    >
      {title && (
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
          {title}
        </p>
      )}
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <span className="text-[12px] font-semibold text-slate-700">{label}</span>
      {children}
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
  onClick,
  disabled,
  destructive,
}: {
  icon: typeof Copy;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={cn(
        'flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3.5 py-2.5',
        'text-[12px] font-medium text-slate-600 transition-colors',
        'hover:border-gray-300 hover:bg-gray-50 hover:text-slate-900',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white',
        destructive && 'hover:border-red-200 hover:bg-red-50 hover:text-red-600'
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {label}
    </button>
  );
}

/**
 * Momentopname van het product zoals het nú in de catalogus staat.
 *
 * Eén keer ophalen per product/variant en daarna uit de cache: de zijbalk springt
 * van regel naar regel terwijl je typt, en dat hoeft het netwerk niet te merken.
 * De nieuwste versie hoort bij de sleutel, zodat een nieuwe publicatie wél opnieuw
 * wordt opgehaald.
 *
 * `undefined` betekent nog aan het laden; `null` dat er niets te vergelijken valt.
 */
const snapshotCache = new Map<string, ProductSnapshot | null>();

function useProductSnapshot(productId: string | null, variantId: string | null) {
  const latestVersionId = useProductVersionStore((state) =>
    productId ? (state.latest[productId]?.versionId ?? '') : ''
  );
  const cacheKey = productId ? `${productId}:${variantId ?? ''}:${latestVersionId}` : null;
  const [snapshot, setSnapshot] = useState<ProductSnapshot | null | undefined>(() =>
    cacheKey ? snapshotCache.get(cacheKey) : null
  );

  useEffect(() => {
    if (!cacheKey || !productId) {
      setSnapshot(null);
      return;
    }
    if (snapshotCache.has(cacheKey)) {
      setSnapshot(snapshotCache.get(cacheKey) ?? null);
      return;
    }

    setSnapshot(undefined);
    let cancelled = false;
    productApi
      .snapshot(productId, variantId ?? undefined)
      .then((result) => {
        snapshotCache.set(cacheKey, result);
        if (!cancelled) setSnapshot(result);
      })
      .catch(() => {
        // Geen product (meer) in de catalogus: dan valt er ook niets te vergelijken.
        snapshotCache.set(cacheKey, null);
        if (!cancelled) setSnapshot(null);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, productId, variantId]);

  return snapshot;
}

export function GroupVersionUpdate({ product, onChange }: {
  product: InvestmentProduct; onChange: (product: InvestmentProduct) => void;
}) {
  const snapshot = useProductSnapshot(product.productId, product.variantId);
  if (!snapshot || snapshot.productVersionId === product.productVersionId) return null;
  return <div className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-xs">
    <strong>Nieuwere catalogusversie</strong>
    <p className="my-2">{snapshot.productName} · versie {snapshot.version}</p>
    <Button size="sm" variant="outline" onClick={async () => {
      if ((Object.keys(product.overrides).length || product.catalogVariants || product.amountCents !== undefined || product.descriptionHtml !== undefined) && !(await confirmAction({
        title: 'Product bijwerken?',
        description: 'Bijwerken vervangt de prijsopbouw, omschrijving en de aanpassingen voor dit product.',
        confirmLabel: 'Bijwerken',
      }))) return;
      onChange({ ...createInvestmentProduct(snapshot, product), id: product.id, display: product.display, showHours: product.showHours });
    }}>Product bijwerken</Button>
    <Button size="sm" variant="ghost" onClick={() => onChange({ ...product, productVersionId: snapshot.productVersionId })}>Melding negeren</Button>
  </div>;
}

function OriginalLine({
  label,
  value,
  changed,
}: {
  label: string;
  value: string;
  changed: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12px] text-[#8A6A3A]">{label}</span>
      <span
        className={cn(
          'text-[12px] tabular-nums',
          changed ? 'font-bold text-[#B8600B]' : 'text-[#8A6A3A]'
        )}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Toont wat het product zelf zegt zodra de regel daarvan afwijkt.
 *
 * Een bedrag in een offerte is een momentopname; sales mag ervan afwijken. Maar
 * dan wil je bij het nakijken wél zien wat de catalogusprijs was.
 */
function ProductOriginal({
  row,
  pricingType,
  currency,
}: {
  row: InvestmentInspectorRow;
  pricingType: PricingType;
  currency: string;
}) {
  const snapshot = useProductSnapshot(row.productId, row.variantId);
  if (!snapshot) return null;

  const original: ProductOriginalValues | null = productOriginalForRow(snapshot, {
    title: row.title,
    pricingType,
    quantity: row.quantity,
    discountPct: row.discountPct,
  });
  if (!original) return null;

  const changed = changedFields(row.cells, pricingType, original);
  if (changed.length === 0) return null;

  return (
    <div className="mt-3 rounded-md border border-[#F0DCBC] bg-[#FEF7EC] px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#B8600B]">
        Afwijkend van de catalogus
      </p>
      <div className="mt-2 flex flex-col gap-1">
        {pricingType === 'hourly' && original.rate != null && (
          <OriginalLine
            label="Uurtarief"
            value={formatCurrency(original.rate, currency)}
            changed={changed.includes('rate')}
          />
        )}
        {pricingType === 'hourly' && original.hours != null && (
          <OriginalLine
            label="Uren"
            value={String(original.hours)}
            changed={changed.includes('hours')}
          />
        )}
        {original.amount != null && (
          <OriginalLine
            label="Bedrag"
            value={formatCurrency(original.amount, currency)}
            changed={changed.includes('amount')}
          />
        )}
      </div>
      <p className="mt-2 text-[11px] leading-[16px] text-[#8A6A3A]">
        Oorspronkelijke waarden uit “{snapshot.productName}”, versie {snapshot.version}.
      </p>
    </div>
  );
}

function VersionLine({
  label,
  current,
  next,
  changed,
}: {
  label: string;
  current: string;
  next: string;
  changed: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12px] text-[#8A6A3A]">{label}</span>
      <span className="text-[12px] tabular-nums text-[#8A6A3A]">
        {changed ? (
          <>
            {current || '—'} → <span className="font-bold text-[#B8600B]">{next}</span>
          </>
        ) : (
          next
        )}
      </span>
    </div>
  );
}

/**
 * De geselecteerde regel staat op een oudere productversie: laat zien wat er
 * verandert en laat sales kiezen tussen bijwerken en negeren.
 *
 * Beide zetten de regel op de nieuwe versie, dus de melding komt pas terug bij een
 * volgende publicatie. Negeren laat de bedragen staan; wijken die af van de nieuwe
 * catalogusprijs, dan telt de regel voortaan als handmatig aangepast.
 */
export function ProductVersionUpdate({
  row,
  pricingType,
  currency,
  version,
  onChange,
}: {
  row: InvestmentInspectorRow;
  pricingType: PricingType;
  currency: string;
  version: OutdatedRowVersion;
  onChange: (rowIndex: number, change: InvestmentRowVersionChange) => void;
}) {
  const snapshot = useProductSnapshot(row.productId, row.variantId);
  const latest = snapshot
    ? productLatestForRow(snapshot, {
        title: row.title,
        pricingType,
        quantity: row.quantity,
        discountPct: row.discountPct,
      })
    : null;
  const changed = latest ? changedFields(row.cells, pricingType, latest) : [];
  const hasPrices = pricingType !== 'fixed';
  const canUpdate = hasPrices && latest !== null;
  const targetVersionId = snapshot?.productVersionId ?? version.versionId;
  const targetVersion = snapshot?.version ?? version.version;
  const money = (value: number | null) => (value == null ? '—' : formatCurrency(value, currency));

  const update = () => {
    if (!latest) return;
    const cells: Record<number, string> =
      pricingType === 'hourly'
        ? { 1: money(latest.rate), 2: String(latest.hours ?? ''), 3: money(latest.amount) }
        : { 1: money(latest.amount) };
    onChange(row.index, { productVersionId: targetVersionId, hasOverrides: false, cells });
    toast.success(`Prijs bijgewerkt naar versie ${targetVersion}`);
  };

  const dismiss = () => {
    onChange(row.index, {
      productVersionId: targetVersionId,
      hasOverrides: row.hasOverrides || changed.length > 0,
    });
    toast.success('Melding genegeerd; de regel houdt zijn huidige prijs');
  };

  let note: string | null = null;
  if (!hasPrices) {
    note = 'Bij een vast bedrag staat de prijs in de totaalregel; controleer die zelf.';
  } else if (snapshot === undefined) {
    note = 'Nieuwe prijs ophalen…';
  } else if (!latest) {
    note = `Deze regel is niet eenduidig terug te vinden in versie ${targetVersion}. Pas de bedragen zo nodig zelf aan.`;
  } else if (changed.length === 0) {
    note = 'De prijs van deze regel blijft gelijk.';
  } else if (row.hasOverrides) {
    note = 'Deze regel is handmatig aangepast; bijwerken overschrijft dat.';
  }

  return (
    <div className="rounded-md border border-[#F0DCBC] bg-[#FEF7EC] px-3 py-2.5">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#B8600B]">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        Nieuwere productversie
      </p>
      <p className="mt-1.5 truncate text-[13px] font-medium text-slate-800">
        {row.title || version.productName}
      </p>
      <p className="text-[12px] text-[#8A6A3A]">
        {version.productName} · {version.usedVersion != null ? `v${version.usedVersion} → ` : ''}v
        {targetVersion}
      </p>

      {canUpdate && latest && (
        <div className="mt-2 flex flex-col gap-1">
          {pricingType === 'hourly' && (
            <>
              <VersionLine
                label="Uurtarief"
                current={row.cells[1] ?? ''}
                next={money(latest.rate)}
                changed={changed.includes('rate')}
              />
              <VersionLine
                label="Uren"
                current={row.cells[2] ?? ''}
                next={String(latest.hours ?? '—')}
                changed={changed.includes('hours')}
              />
            </>
          )}
          <VersionLine
            label="Bedrag"
            current={row.cells[pricingType === 'hourly' ? 3 : 1] ?? ''}
            next={money(latest.amount)}
            changed={changed.includes('amount')}
          />
        </div>
      )}

      {note && <p className="mt-2 text-[11px] leading-[16px] text-[#8A6A3A]">{note}</p>}

      <div className="mt-3 flex items-center gap-3">
        {canUpdate && (
          <button
            type="button"
            onClick={update}
            className="rounded-md border border-[#E6C99C] bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-900 transition-colors hover:border-[#D9B27A] hover:bg-[#FFFBF5]"
          >
            {changed.length > 0 ? 'Prijs bijwerken' : `Bijwerken naar v${targetVersion}`}
          </button>
        )}
        {!(canUpdate && changed.length === 0) && (
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md px-1 py-1.5 text-[12px] font-medium text-[#8A6A3A] transition-colors hover:text-slate-900"
          >
            Negeren
          </button>
        )}
      </div>
    </div>
  );
}

/** Numeric fields commit on blur/Enter; descriptions can update the preview while typing. */
function CellInput({
  label,
  value,
  numeric,
  autoFocus,
  live,
  onCommit,
}: {
  label: string;
  value: string;
  numeric?: boolean;
  autoFocus?: boolean;
  live?: boolean;
  onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    if (draft.trim() !== value.trim()) onCommit(draft);
  };

  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1.5">
      <span className="text-[12px] font-semibold text-slate-700">{label}</span>
      <input
        autoFocus={autoFocus}
        value={draft}
        inputMode={numeric ? 'decimal' : undefined}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          if (live && next.trim() !== value.trim()) onCommit(next);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          }
          if (event.key === 'Escape') setDraft(value);
        }}
        className="h-9 w-full rounded-md border border-gray-200 bg-white px-2.5 text-[13px] text-slate-900 outline-none transition-colors focus:border-slate-400"
      />
    </label>
  );
}

/**
 * Bedragen mogen ook vrije tekst zijn ("Op maat"). Alleen echte bedragen krijgen
 * valuta-opmaak, net als bij het typen in de cel zelf.
 */
function formatAmountForCell(raw: string, currency: string): string {
  const value = raw.trim();
  if (!value || !isCurrencyAmount(value)) return value;
  return formatCurrency(parseCurrencyAmount(value), currency);
}

/**
 * De bewerkbare kolommen van de actieve regel. Welke dat zijn hangt van het
 * prijsmodel af: uurtarief + uren, één bedrag, of niets bij een vast bedrag.
 */
function RowFields({
  row,
  pricingType,
  currency,
  onCommit,
  phase,
}: {
  row: InvestmentInspectorRow;
  pricingType: PricingType;
  currency: string;
  onCommit: (rowIndex: number, cellIndex: number, value: string) => void;
  phase: InvestmentInspectorPhase;
}) {
  if (pricingType === 'hourly') {
    return (
      <>
        <div className="mt-3 flex gap-3">
          <InvestmentRateSelect
            rateKey={row.rateKey}
            amount={row.cells[1] ?? ''}
            currency={currency}
            onSelect={(key, amountCents) => phase.setRowRate?.(row.index, key, amountCents)}
            onCustomChange={(value) => onCommit(row.index, 1, value)}
          />
          <CellInput
            label="Uren"
            numeric
            value={row.cells[2] ?? ''}
            onCommit={(value) => onCommit(row.index, 2, value.trim())}
          />
        </div>
        <div className="mt-3">
          <Field label="Weergave op de offerte">
            <Select value={(row.showHours ?? phase.showHourColumns) ? 'hours' : 'fixed'}
              onValueChange={(value) => phase.setRowShowHours?.(row.index, value === 'hours')}>
              <SelectTrigger className="h-9 text-xs" aria-label="Prijsweergave van deze regel"><SelectValue /></SelectTrigger>
              <SelectContent data-investment-inspector>
                <SelectItem value="hours">Uren en uurtarief</SelectItem>
                <SelectItem value="fixed">Alleen vaste prijs</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="mt-3 flex items-baseline justify-between gap-3">
          <span className="text-[13px] text-slate-500">Regeltotaal</span>
          <span className="text-[14px] font-bold text-slate-900">{row.cells[3] || '—'}</span>
        </div>
      </>
    );
  }

  if (pricingType === 'recurring') {
    return (
      <div className="mt-3">
        <CellInput
          label="Bedrag"
          value={row.cells[1] ?? ''}
          onCommit={(value) => onCommit(row.index, 1, formatAmountForCell(value, currency))}
        />
      </div>
    );
  }

  return (
    <p className="mt-2.5 text-[12px] leading-[18px] text-slate-500">
      Bij een vast bedrag staat er per regel alleen een omschrijving; het bedrag vul je in de
      totaalregel in.
    </p>
  );
}

/**
 * `horizontal` zet de secties naast elkaar. Dat is de vorm voor het onderdok:
 * de bediening blijft dan twee regels hoog in plaats van het halve scherm.
 */
export function InvestmentInspectorBody({
  group,
  phase,
  horizontal,
  autoFocusDescription = false,
  showSelectedRow = true,
}: {
  group: InvestmentInspectorGroup | null;
  phase: InvestmentInspectorPhase | null;
  horizontal?: boolean;
  autoFocusDescription?: boolean;
  showSelectedRow?: boolean;
}) {
  const latestVersions = useProductVersionStore((state) => state.latest);
  const versionNumbers = useProductVersionStore((state) => state.versionNumbers);
  const rowVersion = phase?.row
    ? outdatedRowVersion(
        { latest: latestVersions, versionNumbers },
        phase.row.productId,
        phase.row.productVersionId
      )
    : null;

  return (
    <div className={cn(horizontal && 'flex flex-wrap items-start gap-x-5 gap-y-5 px-4 py-4')}>
      {showSelectedRow && phase?.row && !phase.row.investmentProduct && rowVersion && (
        <Section horizontal={horizontal}>
          <ProductVersionUpdate
            row={phase.row}
            pricingType={phase.pricingType}
            currency={group?.currency ?? 'EUR'}
            version={rowVersion}
            onChange={phase.setRowProductVersion}
          />
        </Section>
      )}

      <Section title="Tabel" horizontal={horizontal}>
        <div className="flex flex-col gap-3">
          {phase && (
            <Field label="Prijsmodel">
              <Select value={phase.pricingType} onValueChange={phase.setPricingType}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(pricingTypes).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          {group && (
            <div className="flex gap-3">
              <Field label="Valuta">
                <Select value={group.currency} onValueChange={group.setCurrency}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tabeltaal">
                <Select value={group.labelLanguage} onValueChange={group.setLabelLanguage}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LABEL_LANG_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}

          {phase && (
            <div className="mt-1 flex flex-col gap-2.5">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="inspector-total-row"
                  checked={phase.showTotalRow}
                  onCheckedChange={(value) => phase.setShowTotalRow(value === true)}
                />
                <Label
                  htmlFor="inspector-total-row"
                  className="cursor-pointer text-[13px] font-normal text-slate-700"
                >
                  Totaalregel tonen
                </Label>
              </div>
              <div
                className="flex items-center gap-2"
                title="Uit: de klant ziet alleen de omschrijvingen en het totaal. De bedragen per regel blijven meetellen."
              >
                <Checkbox
                  id="inspector-row-prices"
                  checked={phase.showRowPrices}
                  onCheckedChange={(value) => phase.setShowRowPrices(value === true)}
                />
                <Label
                  htmlFor="inspector-row-prices"
                  className="cursor-pointer text-[13px] font-normal text-slate-700"
                >
                  Prijs per regel tonen
                </Label>
              </div>
              {phase.hasHourColumns && (
                <div
                  className="flex items-center gap-2"
                  title={
                    phase.showRowPrices
                      ? 'Standaardweergave voor regels zonder eigen instelling. Je kunt de weergave per regel aanpassen.'
                      : 'Uren staan niet op de offerte zolang de prijs per regel verborgen is.'
                  }
                >
                  <Checkbox
                    id="inspector-hour-columns"
                    checked={phase.showHourColumns}
                    disabled={!phase.showRowPrices}
                    onCheckedChange={(value) => phase.setShowHourColumns(value === true)}
                  />
                  <Label
                    htmlFor="inspector-hour-columns"
                    className="cursor-pointer text-[13px] font-normal text-slate-700"
                  >
                    Uren standaard tonen
                  </Label>
                </div>
              )}
            </div>
          )}
        </div>
      </Section>

      {phase && (
        <Section title="Tabelacties" horizontal={horizontal}>
          <div className="flex flex-wrap gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="sm">Alle producten in deze fase</Button></DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Weergave in de offerte</DropdownMenuLabel>
                <DropdownMenuItem onSelect={() => phase.setProductsDisplay?.('collapsed')}>Als één regel tonen</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => phase.setProductsDisplay?.('expanded')}>Met alle regels tonen</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ActionButton
              icon={ArrowUp}
              label="Omhoog"
              onClick={() => phase.move('up')}
              disabled={phase.phaseIndex <= 0}
            />
            <ActionButton
              icon={ArrowDown}
              label="Omlaag"
              onClick={() => phase.move('down')}
              disabled={phase.phaseIndex < 0 || phase.phaseIndex >= phase.phaseCount - 1}
            />
            <ActionButton icon={Copy} label="Dupliceren" onClick={phase.duplicate} />
            <ActionButton
              icon={BookmarkPlus}
              label="Bewaren in bibliotheek"
              onClick={phase.saveToLibrary}
            />
            <ActionButton icon={Trash2} label="Verwijderen" onClick={phase.remove} destructive />
          </div>
        </Section>
      )}

      {showSelectedRow && phase?.row?.investmentProduct && phase.setRowInvestmentProduct && (
        <Section title="Product" horizontal={horizontal}>
          <GroupVersionUpdate product={phase.row.investmentProduct} onChange={phase.setRowInvestmentProduct} />
          <ProductPriceControls product={phase.row.investmentProduct} onChange={phase.setRowInvestmentProduct} currency={group?.currency ?? 'EUR'} />
        </Section>
      )}
      {showSelectedRow && phase?.row && !phase.row.investmentProduct && (
        <Section title="Geselecteerde regel" horizontal={horizontal}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-[13px] font-medium text-slate-800">
              {phase.row.title || 'Lege regel'}
            </span>
            <span className="shrink-0 text-[12px] text-slate-400">
              {phase.row.index} van {phase.row.count}
            </span>
          </div>

          {phase.row.productId && (
            <a
              href={`/products/${phase.row.productId}`}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 underline decoration-slate-300 underline-offset-2 hover:text-slate-900"
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Product uit catalogus openen
            </a>
          )}

          <div className="mt-3">
            <CellInput
              key={`${phase.tableId}:${phase.row.index}`}
              label="Omschrijving"
              value={phase.row.cells[0] ?? ''}
              live
              autoFocus={autoFocusDescription && !phase.row.title}
              onCommit={(value) => phase.setRowCell(phase.row!.index, 0, value)}
            />
          </div>
          <RowFields
            row={phase.row}
            pricingType={phase.pricingType}
            currency={group?.currency ?? 'EUR'}
            onCommit={phase.setRowCell}
            phase={phase}
          />

          {phase.row.productId && !rowVersion && (
            <ProductOriginal
              row={phase.row}
              pricingType={phase.pricingType}
              currency={group?.currency ?? 'EUR'}
            />
          )}
        </Section>
      )}
    </div>
  );
}

const PANEL_WIDTH = 320;
const GAP = 20;
const MIN_FLOAT_HEIGHT = 240;

type FloatPosition = { left: number; top: number; maxHeight: number };

function samePosition(a: FloatPosition | null, b: FloatPosition | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.left === b.left && a.top === b.top && a.maxHeight === b.maxHeight;
}

/**
 * Meet waar het paneel naast het vel past.
 *
 * Het anker is het investeringsblok zelf, niet het venster: bij uitzoomen of een
 * geopend AI-paneel schuift het vel mee en het paneel dus ook. Past het niet naast
 * het vel binnen de canvasruimte, dan geeft dit `null` terug en dokt het paneel
 * onder de canvas.
 */
function useFloatPosition(anchorId: string | null): FloatPosition | null {
  const [position, setPosition] = useState<FloatPosition | null>(null);

  useLayoutEffect(() => {
    if (!anchorId) {
      setPosition(null);
      return;
    }

    let frame = 0;

    const measure = () => {
      frame = 0;
      const anchor = document.querySelector(`[data-block-id="${anchorId}"]`);
      if (!anchor) {
        setPosition(null);
        return;
      }

      const anchorRect = anchor.getBoundingClientRect();
      const scroller = anchor.closest('.overflow-auto');
      const bounds = scroller
        ? scroller.getBoundingClientRect()
        : new DOMRect(0, 0, window.innerWidth, window.innerHeight);

      if (bounds.right - anchorRect.right < PANEL_WIDTH + GAP * 2) {
        setPosition(null);
        return;
      }

      const maxTop = Math.max(bounds.top + GAP, bounds.bottom - MIN_FLOAT_HEIGHT - GAP);
      const top = Math.round(Math.min(Math.max(anchorRect.top, bounds.top + GAP), maxTop));
      const next: FloatPosition = {
        left: Math.round(anchorRect.right + GAP),
        top,
        maxHeight: Math.round(Math.max(MIN_FLOAT_HEIGHT, bounds.bottom - top - GAP)),
      };
      setPosition((current) => (samePosition(current, next) ? current : next));
    };

    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    const observer = new ResizeObserver(schedule);
    const anchor = document.querySelector(`[data-block-id="${anchorId}"]`);
    if (anchor) observer.observe(anchor);
    observer.observe(document.body);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      observer.disconnect();
    };
  }, [anchorId]);

  return position;
}

/**
 * Bediening van de investeringstabel waar de cursor in staat.
 *
 * Standaard zweeft het paneel in de witruimte naast het vel en schuift het mee
 * tijdens scrollen, zodat de instellingen bij het document horen in plaats van bij
 * de schermrand. Past het daar niet (smal venster, uitgezoomd, AI-paneel open), dan
 * dokt hetzelfde paneel onder de canvas in drie kolommen — het valt dus nooit buiten beeld.
 */
export default function InvestmentInspector() {
  const group = useInvestmentInspectorStore((state) => state.group);
  const phase = useInvestmentInspectorStore((state) => state.phase);
  const revealRequest = useInvestmentInspectorStore((state) => state.revealRequest);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const activeId = phase?.tableId ?? group?.groupId ?? null;
  const anchorId = phase?.groupId || group?.groupId || null;
  const position = useFloatPosition(activeId ? anchorId : null);

  // Nieuwe tabel of regel aangeklikt: paneel weer openen.
  useEffect(() => {
    if (activeId) {
      setCollapsed(false);
      setDismissed(false);
    }
  }, [activeId]);

  // Waarschuwingsicoon in een regel aangeklikt: ook een gesloten paneel komt terug.
  useEffect(() => {
    if (revealRequest) {
      setCollapsed(false);
      setDismissed(false);
    }
  }, [revealRequest]);

  if ((!group && !phase) || dismissed) return null;

  const heading = phase ? 'Investeringstabel' : 'Investeringsoverzicht';
  const subheading = phase ? phase.title || 'Naamloze tabel' : 'Hele overzicht';

  if (position) {
    return (
      <div
        className="fixed z-30 flex flex-col overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.16)]"
        style={{
          left: position.left,
          top: position.top,
          width: PANEL_WIDTH,
          maxHeight: position.maxHeight,
        }}
        aria-label="Instellingen investeringstabel"
        data-investment-inspector="true"
      >
        <div className="flex items-start justify-between gap-2 px-4 pb-3 pt-3.5">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
              {heading}
            </p>
            <p className="mt-0.5 truncate text-[14px] font-semibold text-slate-900">{subheading}</p>
          </div>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            title="Paneel sluiten"
            className="-mr-1 shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-gray-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <InvestmentInspectorBody group={group} phase={phase} />
      </div>
    );
  }

  return (
    <div
      className="shrink-0 border-t border-gray-200 bg-white shadow-[0_-8px_24px_rgba(15,23,42,0.08)]"
      aria-label="Instellingen investeringstabel"
      data-investment-inspector="true"
    >
      <button
        type="button"
        onClick={() => setCollapsed((value) => !value)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left"
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
          {heading}
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-slate-700">
          {subheading}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-slate-400 transition-transform',
            collapsed && 'rotate-180'
          )}
          aria-hidden
        />
      </button>
      {!collapsed && (
        <div className="max-h-[40vh] overflow-y-auto">
          <InvestmentInspectorBody group={group} phase={phase} horizontal />
        </div>
      )}
    </div>
  );
}
